import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { submitAssessment, submitBet, submitBetSlip, submitSurvey } from '@/api/game'
import { ConnectionBadge } from '@/components/ConnectionBadge'
import { PhoneShell } from '@/components/PhoneShell'
import { PrimaryButton } from '@/components/ui-kit/Buttons'
import { useSessionSync } from '@/hooks/useSessionSync'
import { useWakeLock } from '@/hooks/useWakeLock'
import { deriveScreen } from '@/lib/deriveScreen'
import { useGameStore } from '@/stores/game'
import { useSessionStore } from '@/stores/session'
import { AssessScreen } from '@/screens/AssessScreen'
import { BalanceScreen } from '@/screens/BalanceScreen'
import { BetScreen } from '@/screens/BetScreen'
import { ConfirmBetsScreen } from '@/screens/ConfirmBetsScreen'
import { FactsScreen } from '@/screens/FactsScreen'
import { HelpOverlay } from '@/screens/HelpOverlay'
import { LobbyScreen } from '@/screens/LobbyScreen'
import { MessageScreen } from '@/screens/MessageScreen'
import { SurveyScreen } from '@/screens/SurveyScreen'
import { RevealScreen } from '@/screens/RevealScreen'
import type { BetPick } from '@/types/game'

/**
 * Die laufende Runde.
 *
 * Welcher Screen erscheint, entscheidet allein deriveScreen aus dem
 * Serverzustand. Es gibt hier keine Navigation und keinen Bildschirm-State -
 * schaltet die Lehrkraft weiter, folgt das Handy zwangsläufig.
 */
export default function SessionPage() {
    const { t } = useTranslation()
    const token = useSessionStore((store) => store.token)
    const { state, loading, error, hydrate } = useGameStore()
    const [helpOpen, setHelpOpen] = useState(false)
    // Nur die Rueckfrage vor der Abgabe (WETTBANDIT-67) - welcher Screen sonst
    // erscheint, entscheidet weiterhin allein deriveScreen.
    const [confirming, setConfirming] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (token !== null) {
            void hydrate()
        }
    }, [token, hydrate])

    useSessionSync(state?.session.id ?? null)
    // Solange die Runde laeuft, soll das Handy nicht in die Bildschirmsperre
    // fallen (WETTBANDIT-69).
    useWakeLock(state !== null)

    if (token === null) {
        return <Navigate to="/" replace />
    }

    if (state === null) {
        return (
            <PhoneShell>
                <header className="flex items-center justify-between">
                    <span className="text-xs font-extrabold tracking-[.25em] text-wb-accent uppercase">
                        {t('app.name')}
                    </span>
                    <ConnectionBadge />
                </header>
                <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                    <p className="text-wb-dim">
                        {loading ? t('session.connecting') : (error ?? t('session.connecting'))}
                    </p>
                    {!loading && error !== null && (
                        <PrimaryButton className="w-auto px-6" onClick={() => void hydrate()}>
                            {t('session.retry')}
                        </PrimaryButton>
                    )}
                </div>
            </PhoneShell>
        )
    }

    /** Schickt eine Abgabe und lädt danach den Zustand nach. */
    const send = async (action: () => Promise<unknown>) => {
        setSubmitError(null)
        setSubmitting(true)

        try {
            await action()
            await hydrate()
        } catch {
            setSubmitError(t('session.submitFailed'))
        } finally {
            setSubmitting(false)
        }
    }

    const screen = deriveScreen(state)

    // Getippt wird in der Reihenfolge, die der Server fuer dieses Handy
    // gewuerfelt hat (WETTBANDIT-67); die Liste selbst bleibt nach Position
    // sortiert, weil Aufdeckung und Bilanz ueber sie laufen.
    const byPosition = new Map(state.matches.map((match) => [match.position, match]))
    // Fehlt die Reihenfolge, wird nach Position getippt. Ohne diesen Rueckfall
    // riss ein fehlendes betOrder das ganze Handy in einen schwarzen Bildschirm
    // - und zwar bei jedem Teilnehmer, nicht nur beim Tippen.
    const betOrder = state.betOrder ?? state.matches.map((match) => match.position)
    const betMatches = betOrder
        .map((position) => byPosition.get(position))
        .filter((match) => match !== undefined)
    const placed = state.matches.filter(
        (match) => match.pick !== null && match.pick !== 'skip',
    ).length

    return (
        <>
            {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}

            {submitError !== null && (
                <p
                    role="alert"
                    className="fixed inset-x-3 top-[max(0.75rem,env(safe-area-inset-top))] z-300 rounded-xl border border-wb-loss/50 bg-wb-loss/15 px-4 py-2.5 text-center text-sm font-bold text-wb-loss backdrop-blur"
                >
                    {submitError}
                </p>
            )}

            {screen === 'survey' && (
                <SurveyScreen
                    onSubmit={(experience, knowledge) =>
                        void send(() => submitSurvey(experience, knowledge))
                    }
                />
            )}

            {screen === 'lobby' && (
                <LobbyScreen
                    nickname={state.participant.nickname}
                    joinCode={state.session.joinCode}
                />
            )}

            {screen === 'wait' && (
                <MessageScreen
                    title={t('wait.title')}
                    headline={t('wait.headline')}
                    body={t('wait.body')}
                    bubble={t('wait.bubble')}
                    pose="teaching"
                />
            )}

            {screen === 'bet' && !confirming && (
                <BetScreen
                    matches={betMatches}
                    onPick={(position, pick: BetPick) => void send(() => submitBet(position, pick))}
                    onSubmit={() => setConfirming(true)}
                    onHelp={() => setHelpOpen(true)}
                />
            )}

            {screen === 'bet' && confirming && (
                <ConfirmBetsScreen
                    placed={placed}
                    submitting={submitting}
                    onConfirm={() => void send(submitBetSlip).then(() => setConfirming(false))}
                    onBack={() => setConfirming(false)}
                />
            )}

            {screen === 'hold-assess' && (
                <MessageScreen
                    title={t('hold.assess.title')}
                    headline={t('hold.assess.title')}
                    body={t('hold.assess.body')}
                    bubble={t('hold.assess.bubble')}
                    pose="standing"
                />
            )}

            {screen === 'assess' && (
                <AssessScreen
                    onSubmit={(guess, confidence) =>
                        void send(() => submitAssessment(guess, confidence))
                    }
                    onHelp={() => setHelpOpen(true)}
                />
            )}

            {screen === 'hold-reveal' && (
                <MessageScreen
                    title={t('hold.reveal.title')}
                    headline={t('hold.reveal.title')}
                    body={t('hold.reveal.body')}
                    bubble={t('hold.reveal.bubble')}
                    pose="winking"
                />
            )}

            {screen === 'reveal' && (
                <RevealScreen matches={state.matches} revealIndex={state.session.revealIndex} />
            )}

            {screen === 'balance' && (
                <BalanceScreen
                    balance={state.balance}
                    matches={state.matches}
                    guess={state.participant.guess}
                />
            )}

            {screen === 'facts' && <FactsScreen />}

            {screen === 'done' && (
                <MessageScreen
                    title={t('done.title')}
                    headline={t('done.headline')}
                    body={t('done.body')}
                    bubble={t('done.bubble')}
                    pose="greeting"
                />
            )}
        </>
    )
}
